import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import { Play, X, ArrowRight, SealCheck } from "@phosphor-icons/react";

// When you have a video, paste a YouTube/Vimeo EMBED url or an MP4 url here.
// Leave empty ("") to show the elegant "coming soon" state.
const FOUNDER_VIDEO_URL = "";
const FOUNDER_PHOTO =
  "https://customer-assets-0z36b82j.emergentagent.net/job_trusted-agents-co/artifacts/jw77895n_IMG_6374.jpeg";

const isEmbed = /youtube|vimeo/.test(FOUNDER_VIDEO_URL);

export default function Founder() {
  const [open, setOpen] = useState(false);
  const hasVideo = Boolean(FOUNDER_VIDEO_URL);

  return (
    <section id="founder" className="bg-warm-white py-24 lg:py-32">
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* photo / video poster */}
        <div className="lg:col-span-6">
          <div className="relative">
            <div className="clip-frame overflow-hidden rounded-2xl border border-black/[0.07]">
              <img
                src={FOUNDER_PHOTO}
                alt="Deep Deep Singh, Founder & Licensed Producer"
                className="w-full h-[420px] sm:h-[520px] object-cover object-top"
                loading="lazy"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
            </div>

            <button
              data-testid="founder-play"
              onClick={() => setOpen(true)}
              className="group absolute inset-0 grid place-items-center"
              aria-label="Play founder introduction"
            >
              <span className="grid place-items-center w-20 h-20 rounded-full bg-warm-white/90 backdrop-blur text-navy shadow-xl transition-all group-hover:scale-110 group-hover:bg-gold">
                <Play weight="fill" size={30} className="ml-1" />
              </span>
            </button>

            <div className="absolute left-4 bottom-4 flex items-center gap-2 rounded-full bg-navy/80 backdrop-blur px-4 py-2 text-warm-white text-xs font-semibold">
              <span className="w-2 h-2 rounded-full bg-gold animate-pulse" />
              {hasVideo ? "Watch: 60-second intro" : "Intro video coming soon"}
            </div>
          </div>
        </div>

        {/* bio */}
        <div className="lg:col-span-6">
          <p className="eyebrow text-gold mb-4 flex items-center gap-3">
            <span className="inline-block w-8 h-px bg-gold" /> Meet Your Agent
          </p>
          <h2 className="font-heading font-extrabold tracking-tighter text-navy text-[clamp(2rem,4.5vw,3.4rem)] leading-[1]">
            A real person behind every policy.
          </h2>
          <div className="mt-5 flex items-center gap-2 text-navy">
            <SealCheck weight="fill" size={20} className="text-gold" />
            <span className="font-semibold">Deep Deep Singh</span>
            <span className="text-brand-slate">· Founder &amp; Licensed Producer</span>
          </div>
          <p className="mt-5 text-brand-slate text-lg leading-relaxed max-w-xl">
            I started WF Services to make insurance feel human again — no jargon, no pressure, just
            honest guidance from someone who picks up the phone. Whether you're protecting a family,
            a mortgage, or a business, I'll walk you through your options and help you choose with confidence.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/" state={{ scrollTo: "booking" }} data-testid="founder-cta"
              className="group inline-flex items-center gap-2 rounded-full bg-navy px-7 py-3.5 text-warm-white font-semibold hover:bg-slate-navy transition-all">
              Book a 15-min intro call
              <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] grid place-items-center bg-midnight/80 backdrop-blur-sm p-5"
            data-testid="founder-modal"
            onClick={() => setOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.94, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.94, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              className="relative w-full max-w-3xl aspect-video rounded-2xl overflow-hidden bg-navy"
              onClick={(e) => e.stopPropagation()}
            >
              <button onClick={() => setOpen(false)} data-testid="founder-modal-close"
                className="absolute top-3 right-3 z-10 grid place-items-center w-10 h-10 rounded-full bg-white/10 text-warm-white hover:bg-white/20 transition-colors">
                <X size={20} />
              </button>

              {hasVideo ? (
                isEmbed ? (
                  <iframe className="w-full h-full" src={FOUNDER_VIDEO_URL} title="Founder introduction"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                ) : (
                  <video className="w-full h-full" src={FOUNDER_VIDEO_URL} controls autoPlay />
                )
              ) : (
                <div className="w-full h-full grid place-items-center text-center px-8 grain relative">
                  <div className="relative z-10">
                    <span className="grid place-items-center w-16 h-16 mx-auto rounded-full bg-gold/15 text-gold mb-5">
                      <Play weight="fill" size={26} className="ml-1" />
                    </span>
                    <h3 className="font-heading font-extrabold text-warm-white text-2xl">Intro video coming soon</h3>
                    <p className="text-light-slate mt-3 max-w-md mx-auto">
                      Deep is recording a short introduction. In the meantime, book a free 15-minute call —
                      it's the next best thing to meeting in person.
                    </p>
                    <Link to="/" state={{ scrollTo: "booking" }} onClick={() => setOpen(false)}
                      className="mt-6 inline-flex items-center gap-2 rounded-full bg-gold px-6 py-3 text-navy font-semibold text-sm hover:brightness-110 transition-all">
                      Book a call <ArrowRight size={16} />
                    </Link>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
